import { useCallback, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import { createFireOnceGuard } from './createFireOnceGuard.ts';
import { evaluateAndMaybeFireMeetingSignal } from './evaluateAndMaybeFireMeetingSignal.ts';
import { getContentKeywords, getTitleKeywords } from './getKeywords.ts';
import type {
	MeetingSignalSource,
	MeetingSignalTrigger,
	TemplateIdAttributes,
} from './meetingSignalTypes.ts';

export interface DetectMeetingSignalInput {
	source: MeetingSignalSource;
	trigger: MeetingSignalTrigger;
	/** Title text to match against the title keyword list (title signal). */
	titleText?: string | null;
	/** Body plain text to match against the content keyword list (content signal). */
	contentText?: string | null;
	/** Selected template identity (template signal, create/edit time only). */
	template?: TemplateIdAttributes | null;
}

export type DetectMeetingSignal = (input: DetectMeetingSignalInput) => boolean;

/**
 * The single hook every surface uses for the `meetingSignal detected`
 * instrumentation (STARB-3763). It owns the cross-cutting plumbing — the
 * analytics event factory, one in-memory fire-once guard per mount (so a
 * repeated caller, e.g. a debounced content stream, still fires at most once),
 * and the locale-resolved keyword lists — and returns a stable `detect`
 * callback. The caller decides *when* to call it and *which* signals to pass;
 * the gate check lives inside `evaluateAndMaybeFireMeetingSignal`.
 *
 * Pass `titleText` to match the title list, `contentText` (already plain text)
 * to match the content list, `template` to test the meeting-template set.
 */
export const useMeetingSignalDetection = (): DetectMeetingSignal => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const guardRef = useRef(createFireOnceGuard());

	const titleKeywords = useMemo(() => getTitleKeywords(intl), [intl]);
	const contentKeywords = useMemo(() => getContentKeywords(intl), [intl]);

	// Explicit PO nudge triggers (twcg-2202-trigger-prototype); the fire decision
	// (title vs content match) lives in evaluateAndMaybeFireMeetingSignal.
	const { fireTrigger: fireTitleTrigger } = useFirePostOfficeTrigger({
		triggerId: 'twc-loom-meeting-nudge-title-trigger',
	});
	const { fireTrigger: fireContentTrigger } = useFirePostOfficeTrigger({
		triggerId: 'twc-loom-meeting-nudge-sidenav-trigger',
	});

	return useCallback(
		({ titleText, contentText, ...rest }: DetectMeetingSignalInput) =>
			evaluateAndMaybeFireMeetingSignal({
				...rest,
				titleText,
				contentText,
				createAnalyticsEvent,
				guard: guardRef.current,
				titleKeywords,
				contentKeywords,
				fireTitleTrigger,
				fireContentTrigger,
			}),
		[createAnalyticsEvent, titleKeywords, contentKeywords, fireTitleTrigger, fireContentTrigger],
	);
};
